import React, { useMemo } from "react";
import styles from "./MultiSelectDropdown.module.css";

/**
 * @param {{
 *   id: string;
 *   searchable: boolean;
 *   searchQuery: string;
 *   onSearchChange: (q: string) => void;
 *   searchInputRef: React.RefObject<HTMLInputElement | null>;
 *   filteredOptions: { value: string; label: string; disabled?: boolean; icon?: React.ReactNode; group?: string }[];
 *   totalOptionCount: number;
 *   value: string[];
 *   highlightedIndex: number;
 *   onHighlight: (index: number) => void;
 *   onSelectOption: (value: string) => void;
 *   maxSelected?: number;
 *   atMax: boolean;
 *   optionColumns?: 1 | 2;
 *   panelRef?: React.RefObject<HTMLDivElement | null>;
 *   className?: string;
 *   style?: React.CSSProperties;
 * }} props
 */
const DropdownPanel = ({
  id,
  searchable,
  searchQuery,
  onSearchChange,
  searchInputRef,
  filteredOptions,
  totalOptionCount,
  value,
  highlightedIndex,
  onHighlight,
  onSelectOption,
  maxSelected,
  atMax,
  optionColumns = 1,
  panelRef,
  className = "",
  style,
}) => {
  const listClass =
    optionColumns === 2
      ? `${styles.optionsList} ${styles.optionsListTwoCol}`
      : styles.optionsList;

  const groupedOptions = useMemo(() => {
    if (!filteredOptions.some((o) => o.group)) return null;
    const groups = [];
    const indexByLabel = new Map();
    filteredOptions.forEach((opt) => {
      const label = opt.group || "Other";
      if (!indexByLabel.has(label)) {
        indexByLabel.set(label, groups.length);
        groups.push({ label, options: [] });
      }
      groups[indexByLabel.get(label)].options.push(opt);
    });
    return groups;
  }, [filteredOptions]);

  const renderOption = (opt, index) => {
    const selected = value.includes(opt.value);
    const disabledByMax = atMax && !selected;
    const rowDisabled = Boolean(opt.disabled) || disabledByMax;
    const highlighted = index === highlightedIndex;
    return (
      <li key={opt.value} role="none" className={optionColumns === 2 ? styles.optionsGridCell : undefined}>
        <button
          type="button"
          role="option"
          aria-selected={selected}
          disabled={rowDisabled}
          title={disabledByMax ? `Maximum ${maxSelected} selected` : undefined}
          className={[
            styles.optionRow,
            highlighted ? styles.optionRowHighlighted : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onMouseEnter={() => onHighlight(index)}
          onClick={() => {
            if (!rowDisabled) onSelectOption(opt.value);
          }}
        >
          {opt.icon ? <span className={styles.optionIcon}>{opt.icon}</span> : null}
          <span className={styles.optionLabel}>{opt.label}</span>
          <span className={styles.check}>{selected ? "✓" : ""}</span>
        </button>
      </li>
    );
  };

  let flatIndex = 0;

  return (
    <div
      ref={panelRef}
      id={id}
      className={[
        styles.dropdown,
        optionColumns === 2 && !groupedOptions ? styles.dropdownTwoCol : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={style}
      role="listbox"
    >
      {searchable && (
        <div className={styles.searchWrap}>
          <input
            ref={searchInputRef}
            type="text"
            className={styles.searchInput}
            placeholder="Filter…"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            aria-label="Filter options"
          />
        </div>
      )}
      <ul className={groupedOptions ? styles.optionsList : listClass} role="presentation">
        {filteredOptions.length === 0 ? (
          <li className={styles.emptyState}>
            {totalOptionCount === 0
              ? "No options available."
              : "No options match your search."}
          </li>
        ) : groupedOptions ? (
          groupedOptions.map((group) => (
            <li key={group.label} role="none" className={styles.optionGroupBlock}>
              <div className={styles.optionGroupHeader}>{group.label}</div>
              <ul
                className={[
                  styles.optionGroupList,
                  optionColumns === 2 ? styles.optionsListTwoCol : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                role="presentation"
              >
                {group.options.map((opt) => {
                  const index = flatIndex;
                  flatIndex += 1;
                  return renderOption(opt, index);
                })}
              </ul>
            </li>
          ))
        ) : (
          filteredOptions.map((opt, index) => renderOption(opt, index))
        )}
      </ul>
      {maxSelected != null && atMax && (
        <div className={styles.maxHint}>
          Maximum {maxSelected} selected.
        </div>
      )}
    </div>
  );
};

export default DropdownPanel;
